import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_TITLES = {
  route: "The Route",
  stays: "Where to Stay",
  eats: "Where to Eat",
  drinks: "Where to Drink",
  experiences: "What to Do",
  skips: "What to Skip",
  daily_rhythm: "Your Daily Rhythm",
  practicals: "Good to Know",
};

const Kicker = ({ children }) => (
  <p className="font-sans text-[0.65rem] uppercase tracking-[0.3em] text-teal">{children}</p>
);

const SectionHeading = ({ index, children }) => (
  <div className="mb-8 flex items-baseline gap-4">
    <span className="font-sans text-xs tracking-[0.2em] text-sand">
      {String(index).padStart(2, "0")}
    </span>
    <h2 className="font-serif text-3xl font-light text-cream sm:text-4xl">{children}</h2>
  </div>
);

// Route: vertical itinerary
const RouteSection = ({ items }) => (
  <ol className="relative ml-2 border-l border-charcoal">
    {items.map((it, i) => (
      <li key={it.id || i} className="relative pb-8 pl-8 last:pb-0" data-testid={`view-route-${i}`}>
        <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full border border-teal bg-ink" />
        <div className="flex flex-wrap items-baseline gap-3">
          <h3 className="font-serif text-xl text-cream">{it.place}</h3>
          {it.nights && (
            <span className="font-sans text-xs uppercase tracking-[0.16em] text-sand">{it.nights} nights</span>
          )}
        </div>
        {it.note && <p className="mt-2 max-w-2xl font-sans text-sm leading-relaxed text-cream/80">{it.note}</p>}
      </li>
    ))}
  </ol>
);

// Generic card list (stays, eats, drinks, experiences)
const CardList = ({ section, items }) => (
  <div className="grid grid-cols-1 gap-px overflow-hidden border border-charcoal bg-charcoal sm:grid-cols-2">
    {items.map((it, i) => (
      <div key={it.id || i} className="bg-ink p-6" data-testid={`view-${section}-${i}`}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-serif text-xl text-cream">{it.name}</h3>
          {(it.cuisine || it.type || it.category || it.style) && (
            <span className="font-sans text-[0.65rem] uppercase tracking-[0.16em] text-teal">
              {it.cuisine || it.type || it.category || it.style}
            </span>
          )}
        </div>
        <p className="mt-1 font-sans text-xs uppercase tracking-[0.14em] text-sand">
          {[it.area, it.meal, it.price_note].filter(Boolean).join(" · ")}
        </p>
        {it.note && <p className="mt-3 font-sans text-sm leading-relaxed text-cream/80">{it.note}</p>}
      </div>
    ))}
  </div>
);

const SkipSection = ({ items }) => (
  <div className="flex flex-col gap-5">
    {items.map((it, i) => (
      <div key={it.id || i} className="border-l-2 border-sand/40 pl-5" data-testid={`view-skips-${i}`}>
        <h3 className="font-serif text-lg text-cream">{it.name}</h3>
        {it.reason && <p className="mt-1 max-w-2xl font-sans text-sm leading-relaxed text-cream/70">{it.reason}</p>}
      </div>
    ))}
  </div>
);

const RhythmSection = ({ items }) => (
  <div className="flex flex-col gap-8">
    {items.map((it, i) => (
      <div key={it.id || i} data-testid={`view-daily_rhythm-${i}`}>
        <div className="flex flex-wrap items-baseline gap-3">
          {it.day_label && <span className="font-sans text-xs uppercase tracking-[0.2em] text-teal">{it.day_label}</span>}
          <h3 className="font-serif text-2xl text-cream">{it.title}</h3>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {["morning", "afternoon", "evening"].map((slot) =>
            it[slot] ? (
              <div key={slot} className="border-t border-charcoal pt-3">
                <p className="font-sans text-[0.6rem] uppercase tracking-[0.2em] text-sand">{slot}</p>
                <p className="mt-2 font-sans text-sm leading-relaxed text-cream/80">{it[slot]}</p>
              </div>
            ) : null
          )}
        </div>
      </div>
    ))}
  </div>
);

const PracticalsSection = ({ items }) => (
  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
    {items.map((it, i) => (
      <div key={it.id || i} data-testid={`view-practicals-${i}`}>
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-teal">{it.label}</p>
        {it.detail && <p className="mt-2 font-sans text-sm leading-relaxed text-cream/80">{it.detail}</p>}
      </div>
    ))}
  </div>
);

const BudgetSection = ({ budget }) => (
  <div className="border border-charcoal p-6 sm:p-8" data-testid="view-budget">
    <div className="flex flex-col divide-y divide-charcoal">
      {(budget.customer_summary || []).map((line, i) => (
        <div key={line.id || i} className="flex items-baseline justify-between gap-4 py-3">
          <div>
            <p className="font-serif text-lg text-cream">{line.label}</p>
            {line.note && <p className="mt-0.5 font-sans text-xs text-sand">{line.note}</p>}
          </div>
          <p className="shrink-0 font-sans text-sm text-teal">{line.amount}</p>
        </div>
      ))}
    </div>
    {budget.customer_total_note && (
      <p className="mt-5 border-t border-charcoal pt-5 font-sans text-sm leading-relaxed text-cream/80">
        {budget.customer_total_note}
      </p>
    )}
  </div>
);

const renderSection = (section, data) => {
  if (section === "route") return <RouteSection items={data} />;
  if (section === "skips") return <SkipSection items={data} />;
  if (section === "daily_rhythm") return <RhythmSection items={data} />;
  if (section === "practicals") return <PracticalsSection items={data} />;
  return <CardList section={section} items={data} />;
};

export const TripView = () => {
  const { secureToken } = useParams();
  const [trip, setTrip] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API}/trip/${secureToken}`);
        if (!res.ok) throw new Error("not found");
        const data = await res.json();
        if (active) {
          setTrip(data);
          setStatus("ok");
          document.title = data.title ? `${data.title} — The Holideviser Edit` : "The Holideviser Edit";
        }
      } catch {
        if (active) setStatus("error");
      }
    })();
    return () => {
      active = false;
    };
  }, [secureToken]);

  if (status === "loading")
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <p className="font-sans text-sm tracking-[0.2em] text-sand">Loading your Edit…</p>
      </div>
    );

  if (status === "error")
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-6 text-center">
        <p className="font-sans text-[0.65rem] uppercase tracking-[0.3em] text-teal">Holideviser</p>
        <h1 className="mt-4 font-serif text-3xl font-light text-cream">This trip isn&apos;t available</h1>
        <p className="mt-3 max-w-sm font-sans text-sm text-sand">
          The link may be private, expired, or not yet published. Please check with your Holideviser concierge.
        </p>
      </div>
    );

  const vis = trip.section_visibility || {};
  const LIST_SECTIONS = ["route", "stays", "eats", "drinks", "experiences", "skips", "daily_rhythm", "practicals"];
  const visibleSections = LIST_SECTIONS.filter((s) => vis[s] && (trip[s] || []).length > 0);

  let idx = 0;
  const next = () => ++idx;

  return (
    <div className="min-h-screen bg-ink text-cream" data-testid="trip-view">
      {/* Save as PDF (hidden in print) */}
      <button
        onClick={() => window.print()}
        data-testid="trip-print-btn"
        className="no-print fixed right-4 top-4 z-40 rounded-none border border-charcoal bg-ink/80 px-4 py-2 font-sans text-[0.6rem] uppercase tracking-[0.18em] text-sand backdrop-blur hover:border-teal hover:text-teal sm:right-6 sm:top-6"
      >
        Save as PDF
      </button>
      {/* Hero */}
      <header className="relative overflow-hidden border-b border-charcoal">
        {trip.hero_image && (
          <div className="absolute inset-0">
            <img src={trip.hero_image} alt="" className="h-full w-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
          </div>
        )}
        <div className="relative mx-auto max-w-3xl px-6 py-24 sm:py-32">
          <Kicker>The Holideviser Edit</Kicker>
          {trip.traveller_name && (
            <p className="mt-6 font-sans text-sm text-sand">Curated for {trip.traveller_name}</p>
          )}
          <h1 className="mt-3 font-serif text-4xl font-light leading-tight text-cream sm:text-6xl" data-testid="trip-title">
            {trip.title}
          </h1>
          {trip.subtitle && <p className="mt-4 max-w-xl font-serif text-xl font-light italic text-cream/80">{trip.subtitle}</p>}
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 font-sans text-xs uppercase tracking-[0.16em] text-sand">
            {trip.destination_label && <span>{trip.destination_label}</span>}
            {trip.trip_dates_label && <span>{trip.trip_dates_label}</span>}
            {trip.duration_label && <span>{trip.duration_label}</span>}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
        {/* Overview / POV */}
        {vis.overview && trip.point_of_view && (
          <section className="mb-16" data-testid="view-overview">
            <Kicker>Our point of view</Kicker>
            <p className="mt-6 whitespace-pre-line font-serif text-xl font-light leading-relaxed text-cream/90 sm:text-2xl">
              {trip.point_of_view}
            </p>
          </section>
        )}

        {visibleSections.map((section) => (
          <section key={section} className="mb-16" data-testid={`view-section-${section}`}>
            <SectionHeading index={next()}>{SECTION_TITLES[section]}</SectionHeading>
            {renderSection(section, trip[section])}
          </section>
        ))}

        {/* Budget */}
        {vis.budget && trip.budget && (trip.budget.customer_summary || []).length > 0 && (
          <section className="mb-16" data-testid="view-section-budget">
            <SectionHeading index={next()}>The Budget</SectionHeading>
            <BudgetSection budget={trip.budget} />
          </section>
        )}

        <footer className="border-t border-charcoal pt-10 text-center">
          <p className="font-sans text-[0.65rem] uppercase tracking-[0.3em] text-teal">Holideviser</p>
          <p className="mt-3 font-serif text-lg font-light italic text-cream/70">
            Curated, not crowdsourced.
          </p>
        </footer>
      </main>
    </div>
  );
};

export default TripView;
